import { createSignal, onMount, For } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import { useUserContext } from '../components/UserContextProvider';
import { useRoleParser } from '../hooks/permissions';
import Text from '../components/Text';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { Citybuild as CitybuildLabel } from '../models/Citybuild';
import { CitybuildHealthState } from '../api/generated/schemas/citybuildHealthState';
import type { CitybuildStatusDto } from '../api/generated/schemas/citybuildStatusDto';
import styles from './Voting.module.css';
import badgeStyles from './CitybuildStatus.module.css';
import { firstError } from '../api/envelope';

const POLL_MS = 15_000;

// GrieferGames runs well under vanilla 20: measured 2026-09-26 over three hours on CB21, median 15.0
// and p05 12.7. A scale anchored on 20 would paint every citybuild as degraded.
const TPS_GOOD = 14;
const TPS_DEGRADED = 10;

// Bots move between citybuilds, and a reading nobody has refreshed says nothing about the citybuild
// as it is now.
const TPS_STALE_MS = 10 * 60_000;

const STATE_KEY: Record<number, string> = {
    [CitybuildHealthState.NUMBER_0]: 'CITYBUILD_STATUS_STATE_UNKNOWN',
    [CitybuildHealthState.NUMBER_1]: 'CITYBUILD_STATUS_STATE_GOOD',
    [CitybuildHealthState.NUMBER_2]: 'CITYBUILD_STATUS_STATE_BAD',
    [CitybuildHealthState.NUMBER_3]: 'CITYBUILD_STATUS_STATE_WHITELISTED',
};

const STATE_BADGE_CLASS: Record<number, string> = {
    [CitybuildHealthState.NUMBER_0]: badgeStyles.badgeUnknown,
    [CitybuildHealthState.NUMBER_1]: styles.outcomeOk,
    [CitybuildHealthState.NUMBER_2]: styles.outcomeBad,
    [CitybuildHealthState.NUMBER_3]: badgeStyles.badgeWhitelisted,
};

export default function CitybuildStatusPage() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const isRoot = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf('Root')];

    const [statuses, setStatuses] = createStore<CitybuildStatusDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [resettingId, setResettingId] = createSignal<number | null>(null);
    const [polledAt, setPolledAt] = createSignal(Date.now());

    const fetchAll = async () => {
        const res = await api.citybuildStatus.listStatuses();
        if (isOk(res.data)) setStatuses(reconcile(res.data.value.statuses ?? [], { key: 'citybuild' }));
        setPolledAt(Date.now());
        if (loading()) setLoading(false);
    };

    // Reads polledAt so a reading that goes stale while its row is otherwise unchanged still drops off.
    const freshTps = (row: CitybuildStatusDto) =>
        row.serverTps != null && row.tpsUpdatedAtUtc != null
            && polledAt() - new Date(row.tpsUpdatedAtUtc).getTime() <= TPS_STALE_MS
            ? row.serverTps
            : null;

    const tpsBadgeClass = (tps: number) =>
        tps >= TPS_GOOD ? styles.outcomeOk
            : tps >= TPS_DEGRADED ? badgeStyles.tpsDegraded
                : styles.outcomeBad;

    const renderTps = (row: CitybuildStatusDto) => {
        const tps = freshTps(row);
        if (tps === null) return '-';
        return (
            <span class={`${styles.outcomeBadge} ${tpsBadgeClass(tps)}`}
                title={t('CITYBUILD_STATUS_TPS_MEASURED', formatDateTime(row.tpsUpdatedAtUtc!))}>
                {tps.toFixed(1)}
            </span>
        );
    };

    onMount(() => {
        void fetchAll();
        useVisibilityPoll(() => void fetchAll(), POLL_MS);
    });

    const resetStatus = async (row: CitybuildStatusDto) => {
        const ok = await showConfirm(t('CITYBUILD_STATUS_RESET_CONFIRM', CitybuildLabel[row.citybuild] ?? String(row.citybuild)));
        if (!ok) return;
        setResettingId(row.citybuild);
        const res = await api.citybuildStatus.resetStatus(row.citybuild);
        setResettingId(null);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('CITYBUILD_STATUS_RESET_FAILED')); return; }
        toast?.success(t('CITYBUILD_STATUS_RESET_DONE'));
        setStatuses(reconcile(res.data.value.statuses ?? [], { key: 'citybuild' }));
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="CITYBUILD_STATUS_TITLE" /></h1>
                <span class={styles.entryCount}>{statuses.length} <Text val="CITYBUILD_STATUS_COUNT" /></span>
            </div>

            <p class={badgeStyles.hint}><Text val="CITYBUILD_STATUS_INTRO" /></p>

            {loading() ? (
                <div class={styles.list}><div class={styles.skeleton} /></div>
            ) : (
                <div class="fcard">
                    <div class={styles.tableWrap}>
                        <table class={styles.logTable}>
                            <thead>
                                <tr>
                                    <th><Text val="CITYBUILD_STATUS_COL_CITYBUILD" /></th>
                                    <th><Text val="CITYBUILD_STATUS_COL_STATE" /></th>
                                    <th><Text val="CITYBUILD_STATUS_COL_DETAIL" /></th>
                                    <th><Text val="CITYBUILD_STATUS_COL_EXPIRES" /></th>
                                    <th><Text val="CITYBUILD_STATUS_COL_TPS" /></th>
                                    <th><Text val="CITYBUILD_STATUS_COL_UPDATED" /></th>
                                    {isRoot() && <th></th>}
                                </tr>
                            </thead>
                            <tbody>
                                <For each={statuses}>{(row) => (
                                    <tr>
                                        <td style={{ 'font-family': 'var(--font-family-mono)' }}>{CitybuildLabel[row.citybuild] ?? row.citybuild}</td>
                                        <td>
                                            <span class={`${styles.outcomeBadge} ${STATE_BADGE_CLASS[row.state] ?? ''}`}>
                                                <Text val={STATE_KEY[row.state] ?? 'CITYBUILD_STATUS_STATE_UNKNOWN'} />
                                            </span>
                                        </td>
                                        <td>{row.rawStatusText ?? '-'}</td>
                                        <td>{row.expiresAtUtc ? formatDateTime(row.expiresAtUtc) : '-'}</td>
                                        <td>{renderTps(row)}</td>
                                        <td>{row.state === CitybuildHealthState.NUMBER_0 ? '-' : formatDateTime(row.updatedAtUtc)}</td>
                                        {isRoot() && (
                                            <td>
                                                <button class="btn btn-secondary btn-sm" title={t('CITYBUILD_STATUS_RESET')}
                                                    disabled={resettingId() === row.citybuild}
                                                    onClick={() => void resetStatus(row)}>
                                                    {resettingId() === row.citybuild
                                                        ? <i class="fa-solid fa-spinner fa-spin" />
                                                        : <i class="fa-solid fa-rotate-left" />}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                )}</For>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
